import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router";
import { useApi } from "@platform/api";

import { RfpDetailsResponse } from "../types/rfpApi";

interface UseRfpDetailsFromParamsOptions<TSelected> {
  enabled?: boolean;
  select?: (data: RfpDetailsResponse) => TSelected;
}

// Options for useRfpDetails hook, RFP ID is required
interface UseRfpDetailsOptions<
  TSelected,
> extends UseRfpDetailsFromParamsOptions<TSelected> {
  rfpId: string;
}

// Returns the React Query key for RFP details. Use this to reference details in cache
export const getRfpDetailsQueryKey = (
  rfpId: string,
): ["rfp", string, "details"] => ["rfp", rfpId, "details"];

// useRfpDetails with selector overload
export function useRfpDetails<TSelected>(options: {
  rfpId: string;
  enabled?: boolean;
  select: (data: RfpDetailsResponse) => TSelected;
}): UseQueryResult<TSelected, Error>;

// useRfpDetails with default select for all RFP details response data
export function useRfpDetails(options: {
  rfpId: string;
  enabled?: boolean;
}): UseQueryResult<RfpDetailsResponse, Error>;

// Hook to fetch RFP details given an RFP ID
export function useRfpDetails<TSelected = RfpDetailsResponse>({
  rfpId,
  enabled = true,
  select,
}: UseRfpDetailsOptions<TSelected>): UseQueryResult<TSelected, Error> {
  const axiosInstance = useApi();

  return useQuery<RfpDetailsResponse, Error, TSelected>({
    queryKey: getRfpDetailsQueryKey(rfpId),
    queryFn: () =>
      axiosInstance
        .get<RfpDetailsResponse>(`brokerportal/v1/rfp/${rfpId}/details`)
        .then((res) => res.data),
    select,
    enabled,
  });
}

// useRfpDetailsFromParams with selector overload
export function useRfpDetailsFromParams<TSelected>(options: {
  enabled?: boolean;
  select: (data: RfpDetailsResponse) => TSelected;
}): UseQueryResult<TSelected, Error>;

// useRfpDetailsFromParams with default select for all RFP details response data
export function useRfpDetailsFromParams(options?: {
  enabled?: boolean;
}): UseQueryResult<RfpDetailsResponse, Error>;

// Hook to fetch RFP details using the RFP ID from route params
export function useRfpDetailsFromParams<TSelected = RfpDetailsResponse>(
  options: UseRfpDetailsFromParamsOptions<TSelected> = {},
): UseQueryResult<TSelected, Error> {
  const { enabled = true, select } = options;
  const { id: rfpId } = useParams<{ id: string }>();
  return useRfpDetails<TSelected>({
    rfpId,
    enabled: enabled !== false && !!rfpId,
    select,
  });
}
